import * as ToastPrimitives from '@radix-ui/react-toast';
import { cva, type VariantProps } from 'class-variance-authority';
import { AlertCircle, AlertTriangle, CheckCircle, Info, X } from 'lucide-react';
import { Children, ComponentPropsWithoutRef, ElementRef, ReactElement, forwardRef, isValidElement } from 'react';

import { Loading } from 'src/shared/components/ui/loaders/loading';
import { cn } from 'src/shared/lib/utils';

const ToastProvider = ToastPrimitives.Provider;

const ToastViewport = forwardRef<
  ElementRef<typeof ToastPrimitives.Viewport>,
  ComponentPropsWithoutRef<typeof ToastPrimitives.Viewport>
>(({ className, ...props }, ref) => (
  <ToastPrimitives.Viewport
    ref={ref}
    className={cn(
      'fixed top-0 z-[100] flex max-h-screen w-full flex-col-reverse p-4 sm:bottom-0 sm:right-0 sm:top-auto sm:flex-col md:max-w-[420px]',
      className
    )}
    {...props}
  />
));
ToastViewport.displayName = ToastPrimitives.Viewport.displayName;

const toastVariants = cva(
  `group mt-1 pointer-events-auto relative flex w-full items-center justify-between space-x-4 overflow-hidden rounded-sm border border-l-8 rounded-none 
  p-4 pl-0 pr-8 shadow-lg transition-all data-[swipe=cancel]:translate-x-0 data-[swipe=end]:translate-x-[var(--radix-toast-swipe-end-x)]
  data-[swipe=move]:translate-x-[var(--radix-toast-swipe-move-x)] data-[swipe=move]:transition-none data-[state=open]:animate-in
  data-[state=closed]:animate-out data-[swipe=end]:animate-out data-[state=closed]:fade-out-80 data-[state=closed]:slide-out-to-right-full
  data-[state=open]:slide-in-from-top-full data-[state=open]:sm:slide-in-from-bottom-full`,
  {
    variants: {
      variant: {
        success: 'border-success-500 bg-green-50',
        info: 'border-blue-400 bg-sky-50',
        warning: 'border-orange-500 bg-orange-50',
        error: 'border-destructive-500 bg-destructive-50',
        loading: 'border-slate-400 bg-neutral-50',
      },
    },
    defaultVariants: {
      variant: 'info',
    },
  }
);

const Toast = forwardRef<
  ElementRef<typeof ToastPrimitives.Root>,
  ComponentPropsWithoutRef<typeof ToastPrimitives.Root> & VariantProps<typeof toastVariants>
>(({ className, variant, children, ...props }, ref) => {
  const actionChildren: ReactElement[] = [];
  const otherChildren: ReactElement[] = [];

  Children.forEach(children, (child) => {
    if (isValidElement(child)) {
      if (child.type === ToastAction) {
        actionChildren.push(child);
      } else {
        otherChildren.push(child);
      }
    }
  });

  return (
    <ToastPrimitives.Root ref={ref} className={cn(toastVariants({ variant }), className)} {...props}>
      <div className="flex items-center">
        <div className="flex-center w-14 pl-1">
          {variant === 'info' && <Info className="stroke-sky-600 fill-white" size={20} />}
          {variant === 'error' && <AlertCircle className="stroke-destructive fill-white" size={20} />}
          {variant === 'warning' && <AlertTriangle className="stroke-orange-500 fill-white" size={20} />}
          {variant === 'success' && <CheckCircle className="stroke-success fill-white" size={20} />}
          {variant === 'loading' && <Loading />}
        </div>

        {otherChildren}
      </div>
      {actionChildren}
      <ToastClose variant={variant} />
    </ToastPrimitives.Root>
  );
});
Toast.displayName = ToastPrimitives.Root.displayName;

const ToastAction = forwardRef<
  ElementRef<typeof ToastPrimitives.Action>,
  ComponentPropsWithoutRef<typeof ToastPrimitives.Action>
>(({ className, ...props }, ref) => (
  <ToastPrimitives.Action
    ref={ref}
    className={cn(
      'inline-flex h-8 shrink-0 items-center justify-center rounded-sm border bg-transparent px-3 text-sm font-medium ring-offset-background transition-colors hover:bg-secondary focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 group-[.destructive]:border-muted/40 group-[.destructive]:hover:border-destructive/30 group-[.destructive]:hover:bg-destructive group-[.destructive]:hover:text-destructive-foreground group-[.destructive]:focus:ring-destructive',
      className
    )}
    {...props}
  />
));
ToastAction.displayName = ToastPrimitives.Action.displayName;

const ToastClose = forwardRef<
  ElementRef<typeof ToastPrimitives.Close>,
  ComponentPropsWithoutRef<typeof ToastPrimitives.Close> & {
    variant?: VariantProps<typeof toastVariants>['variant'];
  }
>(({ className, variant, ...props }, ref) => (
  <ToastPrimitives.Close
    ref={ref}
    className={cn(
      `absolute right-2 top-2 rounded-sm p-1 opacity-0 transition-opacity  
      focus:outline-none group-hover:opacity-100 hover:opacity-100`,
      {
        'hover:bg-green-100 text-success hover:text-success-700': variant === 'success',
        'hover:bg-sky-100 text-blue-500 hover:text-blue-700': variant === 'info',
        'hover:bg-orange-100 text-orange-500 hover:text-orange-700': variant === 'warning',
        'hover:bg-destructive-100 text-destructive hover:text-destructive-700': variant === 'error',
        'hover:bg-neutral-100 text-neutral-500 hover:text-neutral-700': variant === 'loading',
      },
      className
    )}
    toast-close=""
    {...props}
  >
    <X className="h-4 w-4" />
  </ToastPrimitives.Close>
));
ToastClose.displayName = ToastPrimitives.Close.displayName;

const ToastTitle = forwardRef<
  ElementRef<typeof ToastPrimitives.Title>,
  ComponentPropsWithoutRef<typeof ToastPrimitives.Title>
>(({ className, ...props }, ref) => (
  <ToastPrimitives.Title ref={ref} className={cn('text-sm font-semibold', className)} {...props} />
));
ToastTitle.displayName = ToastPrimitives.Title.displayName;

const ToastDescription = forwardRef<
  ElementRef<typeof ToastPrimitives.Description>,
  ComponentPropsWithoutRef<typeof ToastPrimitives.Description>
>(({ className, ...props }, ref) => (
  <ToastPrimitives.Description ref={ref} className={cn('text-xs text-typography-darkgrey', className)} {...props} />
));
ToastDescription.displayName = ToastPrimitives.Description.displayName;

type ToastProps = ComponentPropsWithoutRef<typeof Toast>;

type ToastActionElement = ReactElement<typeof ToastAction>;

export {
  Toast,
  ToastAction,
  type ToastActionElement,
  ToastClose,
  ToastDescription,
  type ToastProps,
  ToastProvider,
  ToastTitle,
  ToastViewport,
};
